import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { DeviceList } from './components/DeviceList';
import { MyPlaces } from './components/MyPlaces';
import { ApiFailure, api, tokens } from './lib/api';
import { clearCache } from './lib/cache';
import type { Me } from './lib/types';
import { Login } from './screens/Login';
import { Today, type ListTab } from './screens/Today';
import { PickupTasks } from './screens/PickupTasks';
import { Earnings } from './screens/Earnings';
import { ChangePassword } from './screens/ChangePassword';
import { SyncBanner } from './components/SyncBanner';
import { useOutbox } from './lib/useOutbox';
import { outbox } from './lib/outbox';
import { ensureNotifyPermission, ensureLocationPermission, watchNetwork } from './lib/native';
import { subscribePush, unsubscribePush } from './lib/push';
import { setServerMap } from './lib/mapStyle';
import { OrderDetail } from './screens/OrderDetail';
import { currentRoute, setRoute, useRouteSegment } from './lib/route';
import { useShowFuture } from './lib/showFuture';
import { dayIn } from '@delivery/shared';
import { CodHandoverDialog, type CodHandoverState } from './components/CodHandoverDialog';
import { ErrorBoundary } from './components/ErrorBoundary';
import { startLiveUpdate } from './lib/liveUpdate';
import { clearTrack, refreshTracking, startTracking, stopTrackingAndFlush } from './lib/tracking';
import { UpdateCheck } from './components/UpdateCheck';
import { ClipboardList, Map as MapIcon, RefreshCw, User, Wallet } from 'lucide-react';
// 地图库 250KB+,懒加载 —— 不看地图的司机不该为它买单,
// 首屏在移动网络下的差异很明显
const MapScreen = lazy(() => import('./screens/MapScreen').then((m) => ({ default: m.MapScreen })));

type Tab = 'today' | 'map' | 'earnings' | 'profile';

export function App() {
  const [authed, setAuthed] = useState(() => Boolean(tokens.get()));
  const [mustChangePassword, setMustChange] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  /*
   * ★ 当前这一屏写进地址栏 —— 刷新之后还在原地。
   *   司机端尤其要:他在路上,网一断页面就重载,
   *   而每次都被扔回「今日」。
   */
  const [tabRaw, setTab] = useRouteSegment(0, 'today');
  const tab = tabRaw as Tab;
  const [pickupOpen, setPickupOpen] = useState(false);
  /*
   * ★ 列表那几个分组的当前选择放在这一层。
   *
   *   打开订单详情时 <Today> 被整个卸载(它和详情是二选一渲染的)——
   *   状态放在 Today 里的话,**返回之后就回到「全部」**。
   *   司机的实际动作是「在『待取货』里点开一单、看完返回、点下一单」,
   *   每次都被弹回全部,等于每看一单要重新找一次分组。
   */
  /*
   * ★ 列表分组也写进地址栏(`#/today/ACCEPTED`)——
   *   它本来就要跨「打开详情再返回」活下来,顺手也跨刷新。
   */
  const [listTabRaw, setListTab] = useRouteSegment(1, 'all');
  const listTab = listTabRaw as ListTab;
  /** 打开的订单详情 —— 列表和地图都往这里塞同一个 id */
  /*
   * ★ 订单详情也进地址栏(`#/order/<id>`)。
   *
   *   司机报的:「订单详情页刷新后就不在这个页面了」。
   *   而他恰恰是最容易刷新的那个:在路上、信号断续、
   *   页面被系统回收之后自动重载 —— 每次都被扔回列表,
   *   然后在几十单里重新找刚才那一单。
   */
  /*
   * ★ 订单详情挂在**当前分组下面**(`#/today/ACCEPTED/<id>`),不是顶替它。
   *
   *   用户报的:「订单详情页刷新后就不在这个页面了」。而第一版把详情
   *   放在第 0 段(`#/order/<id>`)—— 地址是留住了,却把「在哪一栏」
   *   整个挤掉了:返回之后又跳回「全部」,正好把上一条修好的东西撞坏。
   *   详情本来就是列表的下一层,地址也该这么长。
   */
  const [detailId, setDetailIdRaw] = useState<string | null>(() => currentRoute()[2] ?? null);
  const setDetailId = useCallback((id: string | null) => {
    setDetailIdRaw(id);
    const p = currentRoute();
    const base = [p[0] || 'today', p[1] || 'all'];
    setRoute(id ? [...base, id] : base);
  }, []);

  /* 浏览器后退:地址退回列表时,详情要跟着关 */
  useEffect(() => {
    const onHash = () => setDetailIdRaw(currentRoute()[2] ?? null);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  /* 热更新:挂上了就说明这个包能跑。放在早退上面 —— 登录页也要能更新 */
  useEffect(() => {
    void startLiveUpdate();
  }, []);
  const [online, setOnline] = useState(navigator.onLine);
  /**
   * 「显示非当日达订单」—— 列表和地图共用一份(见 lib/showFuture)。
   *
   * ★ 这一行必须待在**所有提前返回的上面**。
   *
   *   下面有 `if (!authed) return <Login/>` 和 `if (mustChangePassword) return …`,
   *   而 Hook 放在它们后面的话,登录前后这个组件的 Hook 数量就对不上 ——
   *   React 当场抛错,**整个司机端白屏**。
   *
   *   我第一版就是放在下面的:登录页正常,一登进去整个 App 空白,
   *   而控制台干干净净(登录夹具等的是「账号框消失」,白屏也算消失,
   *   于是它还以为登录成功了),十条地图用例一起红,
   *   报出来的却是「找不到『地图』按钮」。
   *   Today.tsx 里那段「不能在早退后面加 Hook」的注释写的就是这件事。
   */
  const [showFuture, setShowFuture] = useShowFuture();

  /*
   * ★ 把顶栏的**实际高度**写进一个 CSS 变量。
   *
   * ★ 这一段必须待在**所有早退的上面** —— 和上面那个 Hook 同一个理由。
   *   我第一版把它放在了 `isOnline` 旁边,而那一行在
   *   `if (!authed) return <Login/>` 之后:登录前后 Hook 数量对不上,
   *   React 当场抛错,**整个司机端白屏**。
   *   一轮全量下来 52 条红、跑了 1.1 小时(每条都在等超时),
   *   而报出来的全是「找不到某个元素」。
   *   这个坑在这个文件里已经踩到第三次了。
   *
   *   批量操作条要粘在顶栏**下面**(sticky top: var(--hdr-h))。
   *   这个高度不能写死:顶栏带 `pt-safe`,刘海屏上比普通屏高十几个像素,
   *   而写死之后要么压在顶栏上、要么露出一条会闪的缝。
   *
   *   用 ResizeObserver 而不是量一次:字号、安全区、横竖屏都会改它。
   */
  const headerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const apply = () => {
      document.documentElement.style.setProperty('--hdr-h', `${el.offsetHeight}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [authed, mustChangePassword]);

  const outboxView = useOutbox();

  /*
   * 「手上还有没交给店里的代收现金」—— 每天开工时问一次。
   *
   * ★ 和上面那一行同理,必须待在所有提前返回的上面。
   */
  const [cod, setCod] = useState<CodHandoverState | null>(null);
  /** 本次打开点过「下次提醒」—— 这一次不再弹,下次打开重新问 */
  const codSnoozed = useRef(false);
  /** 上次检查是哪一天(店里的时区)—— 跨天回到前台要重新问 */
  const codDay = useRef<string>('');

  const loadMe = useCallback(async () => {
    try {
      const m = await api<Me>('/driver/me');
      // ★ 底图配置随 me 下发 —— 换底图不必重新构建前端
      setServerMap(m.tenant.tiles ?? null);
      setMe(m);
    } catch (e) {
      if (e instanceof ApiFailure) {
        if (e.code === 'PASSWORD_CHANGE_REQUIRED') return setMustChange(true);
        if (e.status === 401) {
          tokens.clear();
          void clearCache();
          setAuthed(false);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (authed && !mustChangePassword) void loadMe();
  }, [authed, mustChangePassword, loadMe]);

  /*
   * 位置上报(管理端地图上「司机在哪」)。登录后启动循环;
   * 真正采不采由服务端决定 —— 只有手上有单时才采,见 lib/tracking.ts。
   */
  useEffect(() => {
    if (!authed || mustChangePassword) return;
    startTracking();
    void refreshTracking();
  }, [authed, mustChangePassword]);

  /*
   * ★ 手动刷新(顶栏右上角)。各屏大多会自己轮询,但取货清单、订单详情不会 ——
   *   而且司机最常见的疑问就是「调度说给我派了,我这儿怎么没有」。
   *   一下把能对的都对一遍:当前这一屏重新挂载(各屏的加载写在挂载时)、
   *   顶栏的数字、积压的离线操作、要不要采位置。
   */
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const refresh = useCallback(async () => {
    setRefreshing(true);
    setRefreshKey((k) => k + 1);
    try {
      await Promise.allSettled([loadMe(), outbox.sync(), refreshTracking()]);
    } finally {
      setRefreshing(false);
    }
  }, [loadMe]);

  /**
   * 代收现金的提醒 —— 每天第一次打开时问一次。
   *
   * ★ 除了「打开」还看**回到前台**:司机端是装在手机上一直开着的,
   *   很多人根本不关,所谓「第二天打开」实际是「第二天从后台切回来」。
   *   只挂在挂载上的话,那台一直开着的手机永远不会被问第二次。
   *
   * ★ 跨天才重新问,不是每次切回前台都问:一天里切进切出几十次,
   *   每次都弹的话他会条件反射地点掉,而这个弹窗以后是要管真钱的。
   *
   * ★ 这个请求失败**什么都不做**:提醒是锦上添花,送货才是本职。
   *   挂了就静悄悄,不显示错误、也不拦任何事。
   */
  const checkCod = useCallback(async (tz: string) => {
    const today = dayIn(tz);
    if (codDay.current === today) return;
    try {
      const r = await api<CodHandoverState>('/driver/cod-handover');
      codDay.current = today;
      // ★ due 由服务端定(它知道今天问过没有),前端只管点过「下次提醒」没有
      if (r.due && !codSnoozed.current) setCod(r);
    } catch {
      /* 提醒挂了不该影响干活 */
    }
  }, []);

  useEffect(() => {
    const tz = me?.tenant.timezone;
    if (!authed || mustChangePassword || !tz) return;
    void checkCod(tz);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void checkCod(tz);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [authed, mustChangePassword, me?.tenant.timezone, checkCod]);

  // 网络状态用原生插件判断更准 —— navigator.onLine 在移动端会误报
  useEffect(() => {
    let dispose: (() => void) | undefined;
    void watchNetwork(setOnline).then((d) => (dispose = d));
    return () => dispose?.();
  }, []);

  async function toggleDuty() {
    if (!me) return;
    const next = me.driver.dutyStatus === 'OFFLINE' ? 'ONLINE' : 'OFFLINE';

    /*
     * ★ 上线这一刻要定位权限,不要等到「确认取货」那一步。
     *
     *   等到那时候要,系统弹窗会正好盖在司机准备点的按钮上 ——
     *   他人在商家门口、一手拎着包裹,面对的是一个没头没尾的权限请求。
     *   上线是他坐下来准备开工的时刻,这时候问最合适。
     *
     *   拒绝了也让他上线:围栏校验在后端另有一套,
     *   为了权限把人挡在门外是本末倒置。
     */
    if (next === 'ONLINE') {
      await ensureLocationPermission().catch(() => false);
      /*
       * ★ 通知权限也在这一刻要,理由同上。
       *
       *   不主动在打开 app 时弹:第一次进来就问「允许通知吗」是最招人烦的
       *   做法,而且被拒之后再也要不回来 —— 而这个权限决定了他把 app
       *   切到后台之后还知不知道来了新单。
       *
       *   拒绝了照样上线。没有通知只是少一条提醒,不影响他干活。
       */
      const granted = await ensureNotifyPermission().catch(() => false);
      /*
       * ★ 拿到通知权限之后立刻订阅推送。
       *
       *   本地通知只覆盖「app 还开着、只是没在前台」;
       *   app 被彻底关掉之后,只有服务端推送还能触达他 ——
       *   而那需要这台设备先把订阅登记上来。
       *
       *   整个过程静默失败(见 lib/push.ts):浏览器不支持、
       *   推送服务连不上都只意味着「这台设备收不到推送」,
       *   不该影响他干活。
       */
      if (granted) void subscribePush(me.push?.publicKey ?? null);
    }

    // ★ 下线**之前**先把攒着的位置传完 —— 下线之后服务端一个点都不收了
    if (next === 'OFFLINE') await stopTrackingAndFlush();

    try {
      await api('/driver/me/duty', { method: 'PATCH', body: { dutyStatus: next } });
      await loadMe();
      // 上线了、手上又有单的话,马上开始采,别等下一分钟
      void refreshTracking();
    } catch (e) {
      alert(e instanceof ApiFailure ? e.message : '操作失败');
    }
  }

  if (!authed) {
    return (
      <Login
        onDone={(mcp) => {
          setAuthed(true);
          setMustChange(mcp);
        }}
      />
    );
  }

  if (mustChangePassword) {
    return (
      <ChangePassword
        onDone={() => {
          setMustChange(false);
          void loadMe();
        }}
      />
    );
  }

  const isOnline = me?.driver.dutyStatus !== 'OFFLINE';


  return (
    <div className="min-h-dvh bg-slate-50 dark:bg-slate-900">
      {/* 顶栏:上线开关 + 今日概览 */}
      <header
        ref={headerRef}
        className="sticky top-0 z-30 bg-white dark:bg-slate-800 border-b
                         border-slate-200 dark:border-slate-700 pt-safe"
      >
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={toggleDuty}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition
              ${
                isOnline
                  ? 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
              }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-slate-400'}`}
            />
            {isOnline ? '在线' : '离线'}
          </button>

          {/*
            ★ 「非当日达」放在顶栏,紧挨着上线开关 —— 它是一个**全局**的取景框。

              原来它在列表那一屏的标签栏下面,于是:
                · 切到「配送中」「未送达」那几栏时它不见了,而过滤照样在生效
                · 切到地图更看不到它,可地图也跟着这个开关走
              一个还在起作用、却看不见的过滤器,比没有这个功能更糟:
              司机只会觉得「有几单不见了」,而屏幕上没有任何地方能解释。

              放上来之后它和「在线/离线」是同一类东西 —— 都是
              「我现在处在什么状态下看这些单」,任何一屏都看得见、点得到。
          */}
          <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
            <input
              type="checkbox"
              aria-label="显示非当日达订单"
              checked={showFuture}
              onChange={(e) => setShowFuture(e.target.checked)}
              className="h-4 w-4"
            />
            <span>非当日达</span>
          </label>

          <div className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
            {me ? `今日 ${me.today['delivered'] ?? 0} 单已完成` : ''}
            <button
              onClick={() => void refresh()}
              disabled={refreshing}
              aria-label="刷新"
              className="-mr-2 grid h-9 w-9 place-items-center rounded-lg text-slate-500
                         active:bg-slate-100 disabled:opacity-60 dark:active:bg-slate-700"
            >
              <RefreshCw className={`size-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      {/* 离线/同步/冲突状态。冲突不能静默丢弃 —— 司机手上可能还有货 */}
      <SyncBanner view={outboxView} online={online} />

      {/*
        ★ 盖在所有东西上面(z-60),包括详情页。
          这是钱,不是一条可以待会儿再看的通知 —— 而且它问的是
          一个只有两种答案的问题,没有第三条出路。
      */}
      {cod && (
        <CodHandoverDialog
          state={cod}
          onLater={() => {
            // ★ 什么都不往服务端记 —— 「下次提醒」的意思正是「下次还要问」
            codSnoozed.current = true;
            setCod(null);
          }}
          onDone={() => {
            codSnoozed.current = false;
            setCod(null);
          }}
        />
      )}
      <main key={refreshKey}>
        {/*
          ★ 详情页盖在所有 tab 之上 —— 列表点进来和地图点进来是**同一个**。
            分成两个的话,「这一单现在该干嘛」就会有两个答案,
            而且总有一边会先落后。
        */}
        {detailId ? (
          /*
           * ★ 详情单独兜一层。React 在渲染里抛异常会把**整棵树**卸载掉 ——
           *   一个字段读成 undefined 的后果就是整个司机端一片空白,
           *   连返回按钮都没有,而他在路上。
           *   兜住之后最坏也只是「这一单打不开,返回列表」。
           */
          <ErrorBoundary label="这一单" onBack={() => setDetailId(null)}>
            <OrderDetail
              tz={me?.tenant.timezone ?? 'UTC'}
              orderId={detailId}
              onClose={() => setDetailId(null)}
              onChanged={loadMe}
              deliverOnly={me?.policy.deliverOnly ?? false}
              confirmPrechecked={me?.policy.confirmPrechecked ?? false}
              whatsapp={me?.policy.whatsapp ?? false}
            />
          </ErrorBoundary>
        ) : (
          <>
            {tab === 'today' &&
              (pickupOpen ? (
                <PickupTasks onClose={() => setPickupOpen(false)} />
              ) : (
                <Today
                  tab={listTab}
                  onTabChange={setListTab}
                  onOpenPickup={() => setPickupOpen(true)}
                  onOpenOrder={setDetailId}
                  overlay={outboxView.overlay}
                  pendingIds={outboxView.pendingOrderIds}
                  tz={me?.tenant.timezone ?? 'UTC'}
                  assignWithoutAccept={me?.policy.assignWithoutAccept ?? false}
                  canGrab={me?.policy.canGrab ?? false}
                  grabDisabledBy={me?.policy.grabDisabledBy ?? null}
                  specialItems={me?.policy.specialItems ?? false}
                  dutyStatus={me?.driver.dutyStatus ?? 'OFFLINE'}
                  onDutyChanged={() => void loadMe()}
                  deliverOnly={me?.policy.deliverOnly ?? false}
                  confirmPrechecked={me?.policy.confirmPrechecked ?? false}
                  whatsapp={me?.policy.whatsapp ?? false}
                />
              ))}
            {tab === 'map' && (
              <Suspense fallback={<Placeholder title="地图" note="加载中…" />}>
                <MapScreen
                  onOpenOrder={setDetailId}
                  grabDisabledBy={me?.policy.grabDisabledBy ?? null}
                />
              </Suspense>
            )}
            {tab === 'earnings' && <Earnings />}
            {tab === 'profile' && me && (
              <Profile
                me={me}
                onChanged={loadMe}
                onLogout={async () => {
                  // ★ 先把攒着的位置传完(token 清掉就传不了了),再把本机剩下的清掉 ——
                  //   那是上一个人的轨迹,不能留给下一个登录这台手机的人
                  await stopTrackingAndFlush();
                  clearTrack();
                  tokens.clear();
                  // ★ 退登要注销推送 —— 不然下一个用这台手机的人会收到前一个人的单
                  void unsubscribePush();
                  // ★ 缓存里有收件人姓名和电话,不能留给下一个登录这台设备的人
                  void clearCache();
                  setAuthed(false);
                }}
              />
            )}
          </>
        )}
      </main>

      {/* 底部导航 —— 主 CTA 区域,避开 Home Indicator。
          ★ 取货页隐藏:取货页自己有个固定在底部的「确认取货」,两个 fixed bottom-0
            叠在一起,后渲染的 nav 会盖住提交按钮 —— 司机点下去点到的是导航栏。
            E2E 在手机视口下撞出来的,桌面宽屏看不出问题。
            顺带也符合「专注任务时收起标签栏」的常规做法 */}
      {!pickupOpen && (
        <nav
          className="fixed bottom-0 inset-x-0 z-30 bg-white dark:bg-slate-800 border-t
                      border-slate-200 dark:border-slate-700 pb-safe"
        >
          <div className="grid grid-cols-4">
            {(
              [
                ['today', '今日', ClipboardList],
                /*
                ★ 「可领取」不在底部导航里 —— 它是「今日」页顶部的一个状态标签。
                  司机的问题是「有什么活可以接 / 我该去取哪几单 / 我手上还有几单」,
                  这三个问题在同一屏里切换才顺;
                  分成两个页面的话,他得先决定「我要看哪一页」。
              */
                ['map', '地图', MapIcon],
                ['earnings', '收入', Wallet],
                ['profile', '我的', User],
              ] as const
            ).map(([key, label, Icon]) => (
              <button
                key={key}
                onClick={() => {
                  setTab(key);
                  if (key !== 'today') setPickupOpen(false);
                  /*
                   * ★ 切 tab 要**关掉详情页**。
                   *
                   *   详情是盖在所有 tab 之上的一层,而这几个按钮原来只改 `tab` ——
                   *   于是在详情页里点「今日/地图/收入/我的」,底下的 tab 确实换了,
                   *   但屏幕上还是那张详情:**看起来毫无反应**。
                   *   (盖一层东西上去,就得负责让每一个出口都能把它掀掉。)
                   */
                  setDetailId(null);
                }}
                className={`flex flex-col items-center gap-0.5 py-2 ${
                  tab === key ? 'text-brand-600 dark:text-brand-500' : 'text-slate-400'
                }`}
              >
                <Icon className="size-6" />
                <span className="text-xs">{label}</span>
              </button>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}

function Placeholder({ title, note }: { title: string; note: string }) {
  return (
    <div className="grid place-items-center py-24 text-slate-400">
      <div className="text-center">
        <div className="text-lg">{title}</div>
        <div className="text-sm mt-1">{note}</div>
      </div>
    </div>
  );
}

function Profile({
  me,
  onLogout,
  onChanged,
}: {
  me: Me;
  onLogout: () => void;
  onChanged: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  /*
   * ★ 改密码整屏打开,不做成小弹窗:
   *   三个密码框加校验提示,挤在半屏里的手机上根本填不完。
   */
  if (changingPw) {
    return (
      <ChangePassword onDone={() => setChangingPw(false)} onCancel={() => setChangingPw(false)} />
    );
  }

  return (
    // ★ pb-28:底部导航是 fixed 的,不留这段空白的话最后几行
    //   (「只退出这一台设备」、版本号、检查更新)一直压在导航栏底下,怎么滑都看不到
    <div className="p-4 pb-28 space-y-3">
      <div className="rounded-2xl bg-white dark:bg-slate-800 p-5 border border-slate-200 dark:border-slate-700">
        <div className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          {me.driver.name}
        </div>
        <div className="mt-1 text-sm text-slate-500">
          {me.driver.driverNo} · {me.driver.phone}
        </div>
        <dl className="mt-4 space-y-2 text-sm">
          <Row label="车辆" value={`${me.driver.vehicleType ?? '—'} ${me.driver.plateNo ?? ''}`} />
          <Row
            label="手上订单"
            value={`${me.driver.currentOrderCount} / ${me.policy.maxConcurrentOrders}`}
          />
          <Row label="信用分" value={String(me.driver.creditScore)} />
        </dl>

        {/*
          ★ 只让他改车辆信息。
            姓名和工号是调度、商家、客户认人的依据,也是结算单和时间轴上
            署的名 —— 随手改掉的话「这单谁送的」事后就对不上了。
            要改姓名走调度那边,留得下审计。
        */}
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <button
            onClick={() => setEditing(true)}
            className="text-sm text-brand-600 dark:text-brand-400"
          >
            修改车辆信息
          </button>
          {/*
            ★ 改密码的入口原来只有「首次登录被强制改」那一条 ——
              司机想换密码只能打电话找调度,而调度只能重置成一个初始密码,
              等于把密码在电话里念一遍。
          */}
          <button
            onClick={() => setChangingPw(true)}
            className="text-sm text-brand-600 dark:text-brand-400"
          >
            修改密码
          </button>
        </div>
        <p className="mt-1 text-xs text-slate-400">姓名和工号请联系调度修改</p>
      </div>

      {editing && (
        <VehicleForm
          driver={me.driver}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            await onChanged();
          }}
        />
      )}
      <MyPlaces />
      <DeviceList />

      <button
        onClick={onLogout}
        className="w-full h-13 py-3 rounded-xl border border-slate-300 dark:border-slate-600
                   text-slate-700 dark:text-slate-300"
      >
        退出登录
      </button>
      {/* ★ 说清楚只退这一台 —— 多端之后「退出」会让人担心别的设备 */}
      <p className="text-center text-xs text-slate-400">只退出这一台设备</p>
      <UpdateCheck />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-900 dark:text-slate-100">{value}</dd>
    </div>
  );
}

/**
 * 改车辆信息。
 *
 * ★ 车牌全大写、去空格 —— 「ABC 1234」和「abc1234」是同一辆车,
 *   而调度在列表里搜的时候不会想到试第二种写法。规整化在服务端做,
 *   这里只是提示他一下。
 */
function VehicleForm({
  driver,
  onClose,
  onSaved,
}: {
  driver: { vehicleType: string | null; plateNo: string | null };
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [vehicleType, setVehicleType] = useState(driver.vehicleType ?? '');
  const [plateNo, setPlateNo] = useState(driver.plateNo ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await api('/driver/me/profile', {
        method: 'PATCH',
        body: { vehicleType: vehicleType.trim() || null, plateNo: plateNo.trim() || null },
      });
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-black/40" onClick={onClose}>
      <div
        className="w-full rounded-t-2xl bg-white p-5 pb-safe dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-300 dark:bg-slate-600" />
        <h3 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">车辆信息</h3>

        <label className="block text-sm">
          <span className="text-slate-500">车型</span>
          <input
            aria-label="车型"
            value={vehicleType}
            onChange={(e) => setVehicleType(e.target.value)}
            placeholder="摩托车 / 面包车"
            className="mt-1 h-12 w-full rounded-xl border border-slate-300 px-3
                       dark:border-slate-600 dark:bg-slate-900"
          />
        </label>

        <label className="mt-3 block text-sm">
          <span className="text-slate-500">车牌</span>
          <span className="block text-xs text-slate-400">会自动转成大写、去掉空格</span>
          <input
            aria-label="车牌"
            value={plateNo}
            onChange={(e) => setPlateNo(e.target.value)}
            placeholder="ABC1234"
            className="mt-1 h-12 w-full rounded-xl border border-slate-300 px-3 font-mono
                       dark:border-slate-600 dark:bg-slate-900"
          />
        </label>

        {err && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{err}</p>}

        <button
          onClick={() => void save()}
          disabled={busy}
          className="mt-4 h-13 w-full rounded-xl bg-brand-600 py-3 text-lg font-medium text-white
                     disabled:opacity-50"
        >
          {busy ? '保存中…' : '保存'}
        </button>
      </div>
    </div>
  );
}
